import MuiAutocomplete, { type AutocompleteProps as MuiAutocompleteProps } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import { forwardRef } from 'react';

import { InfoIconButton } from '../InfoIconButton';
import { TextField } from '../TextField/TextField';

export type AutocompleteProps<T, F extends boolean = false> = Omit<
  MuiAutocompleteProps<T, false, boolean, F>,
  'renderInput'
> & {
  helperText?: string;
  label?: string;
  required?: boolean;
  error?: string;
  name?: string;
  startAdornment?: React.ReactNode;
  infoTooltip?: string;
  placeholder?: string;
};

const Field = <T,>(
  {
    id,
    label,
    fullWidth = true,
    required,
    error,
    value,
    helperText,
    size = 'small',
    name,
    placeholder = 'Type to search',
    startAdornment,
    infoTooltip,
    loading,
    freeSolo,
    ...rest
  }: AutocompleteProps<T, boolean>,
  ref: React.ForwardedRef<HTMLInputElement>
) => {
  const hasError = Boolean(error);
  return (
    <FormControl
      required={required}
      error={hasError}
      size={size}
      fullWidth={fullWidth}
      sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}
    >
      <Box
        sx={{
          width: fullWidth ? '100%' : null,
        }}
      >
        <MuiAutocomplete<T, false, boolean, boolean>
          id={id}
          fullWidth={fullWidth}
          value={value || null}
          freeSolo={freeSolo}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                autoFocus={rest.autoFocus}
                placeholder={placeholder}
                required={required}
                size={size}
                label={label}
                error={hasError}
                name={name}
                loading={loading}
                slotProps={{
                  htmlInput: {
                    ...params.slotProps.htmlInput,
                    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                      params.slotProps.htmlInput?.onChange?.(event);
                      if (freeSolo) {
                        rest.onChange?.(event, event.target.value, 'createOption');
                      }
                    },
                  },
                  input: {
                    ...params.slotProps.input,
                    startAdornment: startAdornment ? (
                      <InputAdornment position="start">{startAdornment}</InputAdornment>
                    ) : (
                      params.slotProps.input?.startAdornment
                    ),
                  },
                }}
              />
            );
          }}
          {...rest}
          ref={ref}
        />

        {hasError ? <FormHelperText>{error}</FormHelperText> : null}
        {helperText && !hasError ? <FormHelperText>{helperText}</FormHelperText> : null}
      </Box>
      {infoTooltip ? <InfoIconButton tooltip={infoTooltip} sx={{ marginTop: 1 }} /> : null}
    </FormControl>
  );
};

export const Autocomplete = forwardRef(Field) as <T, F extends boolean = false>(
  props: AutocompleteProps<T, F>
) => React.ReactElement;
